/*
 * Bakeyy Bangalore-only gate: location detection + DOM reveal.
 *
 * Server (Liquid) decides who may BUY: only logged-in customers with the
 * blr-price tag get an Add to cart for Bangalore-only products.
 * This script only decides who may SEE those products when the viewer is
 * not such a customer:
 *   - IP geolocation (ipapi.co / ipwho.is, cached in localStorage)
 *   - manual pincode fallback ([data-blr-pincode-form])
 *
 * Status values: pending | bangalore | outside | unknown
 *
 * DOM hooks:
 *   [data-blr-geo="reveal"]        shown only when status == bangalore
 *   [data-blr-gate-root]           product page wrapper (bangalore-only template)
 *     [data-blr-gate-content]      the product itself
 *     [data-blr-blocked-panel]     "not available in your area" panel
 *     [data-blr-checking]          "checking your area" placeholder
 *   form[data-blr-locked]          never submits (belt and braces)
 *
 * Events: document 'bakeyy:geo' (detail = state). Promise: BakeyyGeo.ready
 */
(() => {
  const cfg = window.BakeyyBlr || {};
  if (cfg.enabled === false) return;

  const PREFIXES = String(cfg.pincodePrefixes || '560,562').split(',').map((s) => s.trim()).filter(Boolean);
  const CACHE_MS = (Number(cfg.geoCacheHours) || 24) * 60 * 60 * 1000;
  const PIN_MS = 30 * 24 * 60 * 60 * 1000;
  const GEO_KEY = 'bakeyyGeoV1';
  const PIN_KEY = 'bakeyyPinV1';
  const LEGACY_GEO_KEY = 'hdtBlrBenefitsGeoV2';   // written by snippets/bangalore-login-banner.liquid
  const TIMEOUT_MS = 3500;

  const storage = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) {} },
    remove(key) { try { window.localStorage.removeItem(key); } catch (e) {} }
  };

  const state = { status: 'pending', source: null, city: '', region: '', country: '', pincode: '' };
  let resolveReady;
  const ready = new Promise((resolve) => { resolveReady = resolve; });

  const pinStatus = (pin) => (PREFIXES.some((p) => pin.startsWith(p)) ? 'bangalore' : 'outside');

  const readGeo = (geo) => {
    const city = String(geo?.city || '').trim().toLowerCase();
    const region = String(geo?.region || geo?.region_name || geo?.regionName || '').trim().toLowerCase();
    const country = String(geo?.country_code || geo?.countryCode || geo?.country || '').trim().toLowerCase();
    const cityMatch = city.includes('bangalore') || city.includes('bengaluru');
    const regionOk = !region || region.includes('karnataka');
    let status = 'unknown';
    if (cityMatch && regionOk) status = 'bangalore';
    else if (city || country) status = 'outside';
    return { status, city, region, country };
  };

  const applyDom = () => {
    const root = document.documentElement;
    root.setAttribute('data-bakeyy-geo', state.status);

    // "Might be in Bangalore" = confirmed OR undetermined. Only a confident
    // non-Bangalore result keeps the content hidden.
    const maybeBangalore = state.status === 'bangalore' || state.status === 'unknown';
    document.querySelectorAll('[data-blr-geo="reveal"]').forEach((el) => {
      el.hidden = !maybeBangalore;
    });

    document.querySelectorAll('[data-blr-gate-root]').forEach((gate) => {
      let mode = 'view';
      if (state.status === 'pending') mode = 'pending';
      else if (state.status === 'outside') mode = 'blocked';
      gate.setAttribute('data-blr-gate', mode);
      gate.querySelectorAll(':scope [data-blr-gate-content]').forEach((el) => { el.hidden = mode !== 'view'; });
      gate.querySelectorAll(':scope [data-blr-blocked-panel]').forEach((el) => { el.hidden = mode !== 'blocked'; });
      gate.querySelectorAll(':scope [data-blr-checking]').forEach((el) => { el.hidden = mode !== 'pending'; });
    });
  };

  const apply = (next) => {
    Object.assign(state, next);
    applyDom();
    document.dispatchEvent(new CustomEvent('bakeyy:geo', { detail: { ...state } }));
    if (state.status !== 'pending') resolveReady({ ...state });
  };

  const readCache = (key) => {
    const raw = storage.get(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  };

  const fetchGeo = () => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
    const request = (url) => fetch(url, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => (data && data.success !== false ? data : Promise.reject()));
    const any = typeof Promise.any === 'function'
      ? Promise.any([request('https://ipapi.co/json/'), request('https://ipwho.is/')])
      : new Promise((resolve, reject) => {
        let failed = 0;
        [request('https://ipapi.co/json/'), request('https://ipwho.is/')].forEach((p) => p.then(resolve, () => { failed += 1; if (failed === 2) reject(); }));
      });
    return any.finally(() => window.clearTimeout(timer));
  };

  const resolve = () => {
    if (cfg.customerEligible) return apply({ status: 'bangalore', source: 'customer' });

    const pin = readCache(PIN_KEY);
    if (pin && /^\d{6}$/.test(String(pin.pincode || '')) && Date.now() - (pin.savedAt || 0) < PIN_MS) {
      return apply({ status: pinStatus(String(pin.pincode)), source: 'pincode', pincode: String(pin.pincode) });
    }

    const cached = readCache(GEO_KEY);
    if (cached && cached.status && Date.now() - (cached.savedAt || 0) < CACHE_MS) {
      return apply({ status: cached.status, source: 'cache', city: cached.city || '', region: cached.region || '', country: cached.country || '' });
    }

    const legacy = readCache(LEGACY_GEO_KEY);
    if (legacy && legacy.savedAt && Date.now() - legacy.savedAt < CACHE_MS && legacy.isBangalore) {
      return apply({ status: 'bangalore', source: 'cache' });
    }

    return fetchGeo()
      .then((geo) => {
        const result = readGeo(geo);
        storage.set(GEO_KEY, JSON.stringify({ ...result, savedAt: Date.now() }));
        apply({ ...result, source: 'ip' });
      })
      .catch(() => apply({ status: 'unknown', source: 'ip' }));
  };

  // Only call the geolocation APIs on pages that actually gate something.
  const NEEDS_GEO = '[data-blr-geo], [data-blr-gate-root], [data-blr-pincode-form]';
  let started = false;
  const ensure = () => {
    if (started) return;
    if (!document.querySelector(NEEDS_GEO)) return;
    started = true;
    resolve();
  };

  window.BakeyyGeo = {
    get state() { return { ...state }; },
    ready,
    detect() { if (!started) { started = true; resolve(); } return ready; },
    isBangalore() { return state.status === 'bangalore'; },
    setPincode(value) {
      const pincode = String(value || '').replace(/\D/g, '');
      if (pincode.length !== 6) return null;
      started = true;
      storage.set(PIN_KEY, JSON.stringify({ pincode, savedAt: Date.now() }));
      apply({ status: pinStatus(pincode), source: 'pincode', pincode });
      return state.status;
    },
    clearPincode() {
      storage.remove(PIN_KEY);
      storage.remove(GEO_KEY);
      apply({ status: 'pending', source: null, pincode: '' });
      return resolve();
    },
    refresh() {
      storage.remove(GEO_KEY);
      return resolve();
    }
  };

  // Pincode fallback forms (product page "blocked" panel, section footer).
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-blr-pincode-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('input');
    const message = form.querySelector('[data-blr-pincode-msg]');
    const strings = cfg.strings || {};
    const status = window.BakeyyGeo.setPincode(input?.value);
    if (!status) {
      if (message) message.textContent = strings.pincodeInvalid || 'Please enter a valid 6-digit pincode.';
      form.classList.add('is-invalid');
      return;
    }
    form.classList.remove('is-invalid');
    if (message) message.textContent = status === 'bangalore' ? (strings.pincodeBangalore || '') : (strings.pincodeOutside || '');
  });

  // Never submit a locked product form, whatever the button state.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-blr-locked')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    form.closest('[data-type="buy-buttons"]')?.querySelector('[data-blr-gate-panel]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, true);

  // Re-apply after the theme swaps DOM (section rendering, quick view, cart drawer).
  let timer = null;
  const observer = new MutationObserver(() => {
    if (!started) { ensure(); return; }
    if (state.status === 'pending') return;
    window.clearTimeout(timer);
    timer = window.setTimeout(applyDom, 80);
  });

  const start = () => {
    applyDom();
    observer.observe(document.body, { childList: true, subtree: true });
    ensure();
  };
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
